import React, { useState, useEffect, useCallback, useRef } from 'react';
import useUnsavedChanges from '../hooks/useUnsavedChanges';
import { showAppAlert, requestJson as request } from '../api';
import { useDialogFocus } from '../ui';
import { openPlanning } from '../app/workspace';
import { snap, localValue, unixValue } from './presentation';
import '../JobPlanner.css';
export { default as SegmentEditor } from './SegmentEditor';
export function JobEditor({
  job,
  close,
  saved,
  remove
}) {
  const [busy,setBusy]=useState(false);
  const [form, setForm] = useState({
    ...job,
    display_name: job.display_name || job.name,
    dispo_start: localValue(job.dispo_start),
    dispo_end: localValue(job.dispo_end)
  });
  const requestClose = useUnsavedChanges(form, close);
  const focusRef = useRef(null);
  useDialogFocus(focusRef, requestClose);
  const submit = async e => {
    e.preventDefault();
    if(busy)return;setBusy(true);
    try {
      await request('/planning/jobs/' + job.id, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...form,
          dispo_start: unixValue(form.dispo_start),
          dispo_end: unixValue(form.dispo_end)
        })
      });
      requestClose.markSaved(form);
      saved();
    } catch (err) {
      showAppAlert(err.message, {danger: true});
    }finally{setBusy(false);}
  };
  return <div className="jp-inspector"><form ref={focusRef} tabIndex={-1} role="dialog" aria-label="Job bearbeiten" className="jp-modal" onSubmit={submit} onMouseDown={e => e.stopPropagation()}><header><div><small>JOBEINSTELLUNGEN</small><h2>{job.display_name || job.name}</h2></div><button type="button" aria-label="Schließen" onClick={requestClose}>×</button></header><div className="jp-form">
  <label>Anzeigename<input required value={form.display_name} onChange={e => setForm({
            ...form,
            display_name: e.target.value
          })} /></label><label>Kunde<input value={form.customer || ''} onChange={e => setForm({
            ...form,
            customer: e.target.value
          })} /></label>
  <label>Dispo-Beginn<input type="datetime-local" value={form.dispo_start} onChange={e => setForm({
            ...form,
            dispo_start: e.target.value
          })} /></label><label>Dispo-Ende<input type="datetime-local" value={form.dispo_end} onChange={e => setForm({
            ...form,
            dispo_end: e.target.value
          })} /></label>
  <label>Transport<select value={form.transport_type || 'OWN'} onChange={e => setForm({
            ...form,
            transport_type: e.target.value
          })}><option value="OWN">Eigentransport</option><option value="FORWARDER">Spedition</option><option value="CUSTOMER">Selbstabholer</option></select></label><label className="check"><input type="checkbox" checked={Boolean(form.self_pickup)} onChange={e => setForm({
            ...form,
            self_pickup: e.target.checked
          })} /> Selbstabholer</label>
 </div><footer><button type="button" className="danger" onClick={() => remove(job)}>Job löschen</button><button type="button" onClick={async () => {
          await openPlanning(job);
        }}>In Planung öffnen</button><span /><button type="button" onClick={requestClose}>Abbrechen</button><button className="primary" disabled={busy}>{busy?'Speichert …':'Speichern'}</button></footer></form></div>;
}
export function GroupEditor({
  group,
  close,
  saved,
  remove
}) {
  const [busy,setBusy]=useState(false);
  const [name, setName] = useState(group.name);
  const requestClose = useUnsavedChanges(name, close);
  const focusRef = useRef(null);
  useDialogFocus(focusRef, requestClose);
  return <div className="jp-overlay" onMouseDown={requestClose}><form ref={focusRef} tabIndex={-1} role="dialog" aria-label="Ladung bearbeiten" className="jp-modal jp-small-modal" onSubmit={async e => {
      e.preventDefault();
      if(busy)return;setBusy(true);
      try {
        await request('/planning/groups/' + group.id, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name,
            sort_order: group.sort_order,
            version: group.version
          })
        });
        requestClose.markSaved(name);saved();
      } catch (err) {
        showAppAlert(err.message);
      }finally{setBusy(false)}
    }} onMouseDown={e => e.stopPropagation()}><header><div><small>LADUNG</small><h2>Ladung bearbeiten</h2></div><button type="button" aria-label="Schließen" onClick={requestClose}>×</button></header><div className="jp-form"><label className="wide">Name<input required value={name} onChange={e => setName(e.target.value)} /></label></div><footer><button type="button" className="danger" onClick={() => remove(group)}>Ladung löschen</button><span /><button type="button" onClick={requestClose}>Abbrechen</button><button className="primary" disabled={busy}>{busy?'Speichert …':'Speichern'}</button></footer></form></div>;
}
export function Onboarding({
  data,
  close,
  saved,
  initialSource = 'MANUAL'
}) {
  const now = snap(Date.now() / 1000),
    step = 2;
  const [busy, setBusy] = useState(false),
    [createError, setCreateError] = useState('');
  const [form, setForm] = useState({
    external_source: initialSource,
    external_id: '',
    display_name: '',
    customer: '',
    dispo_start: localValue(now),
    dispo_end: localValue(now + 86400),
    transport_type: 'OWN',
    self_pickup: false
  });
  const [externalJobs, setExternalJobs] = useState([]),
    [search, setSearch] = useState(''),
    [externalPage, setExternalPage] = useState(1),
    [hasMore, setHasMore] = useState(false),
    [externalLoading, setExternalLoading] = useState(false),
    [externalError, setExternalError] = useState(''),
    [externalStats, setExternalStats] = useState(null),
    [eventworxCacheStatus, setEventworxCacheStatus] = useState(null);
  const loadEventworxCacheStatus = useCallback(async () => {
    try {
      setEventworxCacheStatus(await request('/planning/imports/EVENTWORX/status'));
    } catch {}
  }, []);
  const importRequest=useRef(null),importSequence=useRef(0);
  useEffect(()=>()=>{importSequence.current++;importRequest.current?.abort()},[]);
  const loadExternal = useCallback(async (page = 1, append = false, forceRefresh = false) => {
    if (form.external_source !== 'EVENTWORX') return;
    const sequence=++importSequence.current;importRequest.current?.abort();const controller=new AbortController();importRequest.current=controller;
    setExternalLoading(true);
    setExternalError('');
    try {
      const params = new URLSearchParams({
        q: search.trim(),
        page: String(page)
      });
      if (forceRefresh) params.set('refresh', '1');
      const result = await request('/planning/imports/EVENTWORX?' + params.toString(),{signal:controller.signal});
      if(sequence!==importSequence.current)return;
      setExternalJobs(old => append ? [...old, ...result.items.filter(item => !old.some(existing => existing.externalId === item.externalId))] : result.items);
      setExternalPage(page);
      setHasMore(Boolean(result.hasMore));
      setExternalStats({
        sourceCount: result.sourceCount || 0,
        detailCandidates: result.detailCandidates || 0,
        detailErrors: result.detailErrors || 0,
        total: result.total || 0,
        cached: Boolean(result.cached),
        cacheAgeSeconds: result.cacheAgeSeconds || 0,
        cacheFetchedAt: result.cacheFetchedAt || null,
        refreshing: Boolean(result.refreshing)
      });
      setEventworxCacheStatus({
        cached: true,
        refreshing: Boolean(result.refreshing),
        fetchedAt: result.cacheFetchedAt || null
      });
    } catch (err) {
      if(sequence!==importSequence.current||err.name==='AbortError')return;
      setExternalError(err.message);
      if (!append) setExternalJobs([]);
    } finally {
      if(sequence===importSequence.current)setExternalLoading(false);
    }
  }, [form.external_source, search]);
  useEffect(() => {
    if (step !== 2 || form.external_source !== 'EVENTWORX') return undefined;
    const sequenceCounter = importSequence;
    const timer = window.setTimeout(() => loadExternal(1, false), 300);
    return () => {window.clearTimeout(timer);sequenceCounter.current++;importRequest.current?.abort()};
  }, [step, form.external_source, loadExternal]);
  useEffect(() => {
    if (step !== 2 || form.external_source !== 'EVENTWORX') return undefined;
    loadEventworxCacheStatus();
    const timer = window.setInterval(loadEventworxCacheStatus, 15000);
    return () => window.clearInterval(timer);
  }, [step, form.external_source, loadEventworxCacheStatus]);
  const requestClose = useUnsavedChanges(form, close);
  const focusRef = useRef(null);
  useDialogFocus(focusRef, requestClose);
  const create = async () => {
    if (busy) return;
    if (form.external_source === 'MANUAL' && unixValue(form.dispo_end) <= unixValue(form.dispo_start)) {
      setCreateError('Das Ende muss nach dem Beginn liegen.');
      return;
    }
    setBusy(true);
    setCreateError('');
    try {
      let created;
      if (form.external_source !== 'MANUAL') created = await request('/planning/imports/' + form.external_source + '/' + encodeURIComponent(form.external_id), {
        method: 'POST'
      });else created = await request('/planning/jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...form,
          dispo_start: unixValue(form.dispo_start),
          dispo_end: unixValue(form.dispo_end)
        })
      });
      saved(created);
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const externalDate = unix => unix ? new Intl.DateTimeFormat('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(Number(unix) * 1000)) : 'Kein Datum';
  const cacheStamp = eventworxCacheStatus?.fetchedAt || externalStats?.cacheFetchedAt;
  const cacheStampLabel = cacheStamp ? new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(cacheStamp)) : 'noch nicht geladen';
  const cacheRefreshing = Boolean(eventworxCacheStatus?.refreshing || externalStats?.refreshing);
  return <div className="jp-overlay onboarding"><section ref={focusRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={initialSource === 'MANUAL' ? 'Job anlegen' : 'Job importieren'} className="jp-wizard"><header><div><small>JOBS</small><h1>{initialSource === 'MANUAL' ? 'Job anlegen' : 'Job importieren'}</h1></div><button aria-label="Schließen" onClick={requestClose}>×</button></header><main>
 {initialSource !== 'MANUAL' && <label className="ui-field">Quelle<select value={form.external_source} disabled={busy} onChange={e => {
            setForm({
              ...form,
              external_source: e.target.value,
              external_id: ''
            });
            setExternalJobs([]);
            setSearch('');
          }}><option value="EVENTWORX">Eventworx</option><option value="RENTMAN">Rentman</option></select></label>}
 {step === 2 && form.external_source === 'EVENTWORX' && <div className="jp-import-picker">
  <div className="jp-import-search-row"><label className="jp-import-search"><span>Eventworx-Lieferscheine durchsuchen</span><input autoFocus type="search" value={search} onChange={e => {
                setSearch(e.target.value);
                setForm(old => ({
                  ...old,
                  external_id: ''
                }));
              }} placeholder="Jobname oder Kunde eingeben..." /></label><button type="button" className="jp-import-refresh" title="EventWorx neu laden" aria-label="EventWorx neu laden" disabled={externalLoading} onClick={() => loadExternal(1, false, true)}>&#8635;</button></div>
  <p className="jp-import-cache-state">Stand: {cacheStampLabel}{cacheRefreshing && <> - Wird aktualisiert... <span className="jp-import-spinner" aria-hidden="true" /></>}</p>
  <p className="jp-import-hint">Aktive, nicht archivierte Lieferscheine: Ende frühestens vor 24 Stunden, Beginn spätestens in 14 Tagen. Chronologisch sortiert.</p>
  {externalError && <div className="jp-import-error">{externalError}</div>}
  <div className="jp-import-list" role="listbox" aria-label="Eventworx-Jobs">{externalJobs.map(job => <button type="button" role="option" aria-selected={form.external_id === job.externalId} disabled={job.imported} className={'jp-import-job ' + (form.external_id === job.externalId ? 'selected' : '')} onClick={() => setForm(old => ({
              ...old,
              external_id: job.externalId
            }))} key={job.externalId}>
   <span className="jp-import-date"><b>{externalDate(job.start)}</b><small>bis {externalDate(job.end)}</small></span><span className="jp-import-info"><b>{job.externalName}</b><small>{job.customer || 'Ohne Kunde'}</small></span><span className={job.imported ? 'imported' : 'select-state'}>{job.imported ? 'Bereits importiert' : form.external_id === job.externalId ? 'Ausgewählt' : 'Auswählen'}</span>
  </button>)}</div>
  {externalLoading && <div className="jp-import-loading">Eventworx-Lieferscheine werden geladen…</div>}{!externalLoading && !externalError && !externalJobs.length && <div className="jp-import-empty">Keine passenden aktuellen Lieferscheine gefunden.{externalStats && <small> Eventworx lieferte {externalStats.sourceCount} Listeneinträge; {externalStats.detailCandidates} wurden als mögliche Lieferscheine geprüft; {externalStats.detailErrors} Detailabrufe schlugen fehl.</small>}</div>}
  {hasMore && <button type="button" className="jp-load-more" disabled={externalLoading} onClick={() => loadExternal(externalPage + 1, true)}>Weitere Jobs laden</button>}
 </div>}
 {form.external_source === 'RENTMAN' && <div className="jp-form"><label>Externe Auftrags-ID<input autoFocus value={form.external_id} onChange={e => setForm({
              ...form,
              external_id: e.target.value
            })} /></label></div>}
 {form.external_source === 'MANUAL' && <div className="jp-form"><label>Anzeigename<input autoFocus={form.external_source === 'MANUAL'} value={form.display_name} onChange={e => setForm({
              ...form,
              display_name: e.target.value
            })} /></label><label>Kunde<input value={form.customer} onChange={e => setForm({
              ...form,
              customer: e.target.value
            })} /></label><label>Dispo-Beginn<input type="datetime-local" value={form.dispo_start} onChange={e => setForm({
              ...form,
              dispo_start: e.target.value
            })} /></label><label>Dispo-Ende<input type="datetime-local" value={form.dispo_end} onChange={e => setForm({
              ...form,
              dispo_end: e.target.value
            })} /></label></div>}
 {form.external_source === 'MANUAL' && <div className="jp-form"><label>Transport<select value={form.transport_type} onChange={e => setForm({
              ...form,
              transport_type: e.target.value
            })}><option value="OWN">Eigentransport</option><option value="FORWARDER">Spedition</option><option value="CUSTOMER">Selbstabholer</option></select></label><label className="check"><input type="checkbox" checked={form.self_pickup} onChange={e => setForm({
              ...form,
              self_pickup: e.target.checked
            })} /> Selbstabholer</label></div>}
 {createError && <p role="alert" className="jp-import-error">{createError}</p>}
 </main><footer><button disabled={busy} onClick={requestClose}>Abbrechen</button><span /><button className="primary" disabled={busy || (form.external_source === 'MANUAL' ? !form.display_name.trim() || !form.dispo_start || !form.dispo_end : !form.external_id)} onClick={create}>{busy ? 'Wird gespeichert …' : form.external_source === 'MANUAL' ? 'Job anlegen' : 'Importieren'}</button></footer></section></div>;
}
